import { Loader } from '@fountain/fountain-ui-components';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { Event, Unauthorized } from 'api-clients/monolith';
import { UpdateModeTypes } from 'Calendar/CreateEvent/types';
import { useSimpleToggle } from 'hooks';
import React, { useCallback, useEffect, useRef, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { StyledPagination } from 'components/StyledPagination';
import { addMessageAction } from 'containers/FlashMessage/actions';

import {
  EventQuery,
  EventStageOption,
  EventUserOption,
  useDeleteEvent,
  useEvents,
  useUsersOptions,
} from '../hooks';
import { AddEventModalContainer } from './AddEventModalContainer';
import { eventManagerContext, EventModalVariant } from './context';
import { DeleteEventModal } from './DeleteEventModal';
import { EventsContainer } from './EventsContainer';
import { Header } from './Header';
import { messages } from './messages';

const useStyles = makeStyles(theme =>
  createStyles({
    pageWrapper: {
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(1),
      },
      [theme.breakpoints.up('md')]: {
        gap: theme.spacing(2.5),
      },
    },
    headerWrapper: {
      display: 'flex',
      gap: theme.spacing(1.5),
      flexDirection: 'column',
    },
  }),
);

export type EventManagerProps = Record<string, never>;

export const EventManager: VFC<EventManagerProps> = () => {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();
  const [pages, setPages] = useState<Event[][]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event>();
  const [stageOptions, setStageOptions] = useState<EventStageOption[]>([]);
  const [userOptions, setUserOptions] = useState<EventUserOption[]>([]);
  const { result: userOptionsResult } = useUsersOptions();
  const hasInitialized = useRef(false);
  const { filters, setFilters, data, lastResult, refetch, totalPages } =
    useEvents();

  useEffect(() => {
    if (!hasInitialized.current) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlPage = urlParams.get('page');

      if (urlPage) {
        const pageNumber = parseInt(urlPage, 10);
        if (!Number.isNaN(pageNumber) && pageNumber > 0) {
          setFilters(prev => ({ ...prev, page: pageNumber - 1 }));
        }
      }
      hasInitialized.current = true;
    }
  }, [setFilters]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const currentUrlPage = urlParams.get('page');
    const expectedUrlPage = (filters.page + 1).toString();

    if (currentUrlPage !== expectedUrlPage) {
      urlParams.set('page', expectedUrlPage);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [filters.page]);

  const {
    off: setAddEventModalClosed,
    on: setAddEventModalOpen,
    showContent: addEventModalOpen,
  } = useSimpleToggle();
  const {
    on: setDeleteEventModalOpen,
    off: setDeleteEventModalClosed,
    showContent: deleteEventModalOpen,
  } = useSimpleToggle();
  const [eventModalVariant, setEventModalVariant] =
    useState<EventModalVariant>('create');
  const scrollContent = useRef<null | HTMLDivElement>(null);
  // TODO can whoami drive canManage?
  const canManage = false;

  const onSuccess = () => {
    setDeleteEventModalClosed();
    setSelectedEvent(undefined);
    void refetch();

    dispatch(
      addMessageAction(intl.formatMessage(messages.deleteSuccess), 'success'),
    );
  };

  const onError = (response: Unauthorized | undefined) => {
    setDeleteEventModalClosed();
    setSelectedEvent(undefined);
    dispatch(
      addMessageAction(
        response?.error ?? intl.formatMessage(messages.error),
        'error',
      ),
    );
  };

  const { mutation: deleteEvent, result: deleteResult } = useDeleteEvent({
    onSuccess,
    onError,
  });

  const handleDeleteEvent = (externalId: string) => {
    deleteEvent(externalId, {
      delete_mode: UpdateModeTypes.UNBOOKED,
    });
  };

  const handleCreateEvent = () => {
    setEventModalVariant('create');
    setAddEventModalOpen();
  };

  useEffect(() => {
    if (userOptionsResult.status === 'ready') {
      setUserOptions(userOptionsResult.data);
    }
  }, [userOptionsResult]);

  const updateFiltersEffects = useCallback(
    (newFilters: EventQuery) => {
      setFilters({ ...newFilters, page: 0 });
    },
    [setFilters],
  );

  useEffect(() => {
    setPages(data.length > 0 ? [data] : []);
  }, [data]);

  const handleCloseAddEventModal = () => {
    setAddEventModalClosed();
    setSelectedEvent(undefined);
  };

  const handleCloseDeleteEventModal = () => {
    setSelectedEvent(undefined);
    setDeleteEventModalClosed();
  };

  const handleRefetch = () => {
    void refetch();
    setEventModalVariant('create');
  };

  return (
    <>
      {lastResult.status === 'loading' && <Loader fullScreen />}
      <eventManagerContext.Provider
        value={{
          pages,
          setPages,
          setEventModalVariant,
          eventModalVariant,
          stageOptions,
          setStageOptions,
          userOptions,
          selectedEvent,
          setSelectedEvent,
          filters,
          canManage,
        }}
      >
        <div className={styles.pageWrapper} ref={scrollContent}>
          <div className={styles.headerWrapper}>
            <Typography variant="h3">
              <FormattedMessage {...messages.scheduledSessions} />
            </Typography>
          </div>
          <Header
            onUpdate={updateFiltersEffects}
            onHandleCreate={handleCreateEvent}
          />
          {lastResult.status !== 'loading' && (
            <EventsContainer
              setDeleteEventModalOpen={setDeleteEventModalOpen}
              setAddEventModalOpen={setAddEventModalOpen}
              initialLoad={lastResult.status === 'ready'}
            />
          )}

          <StyledPagination
            key={`pag-${filters.date}-${filters.page}-${totalPages}`}
            currentPage={filters.page + 1}
            lastPage={totalPages || 1}
            isLoading={lastResult.status === 'loading'}
            recordsPerPage={10}
            setCurrentPage={useCallback(
              (page: number) => {
                // Set loading immediately to reflect user action
                setFilters(prev => ({ ...prev, page: page - 1 }));
              },
              [setFilters],
            )}
          />
        </div>

        {addEventModalOpen && (
          <AddEventModalContainer
            refetch={handleRefetch}
            onClose={handleCloseAddEventModal}
          />
        )}
        {deleteEventModalOpen && (
          <DeleteEventModal
            eventExternalId={selectedEvent?.external_id}
            onCancel={handleCloseDeleteEventModal}
            deleteEvent={handleDeleteEvent}
            isLoading={deleteResult.isLoading}
          />
        )}
      </eventManagerContext.Provider>
    </>
  );
};
